import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { useAuth } from '@/context/AuthContext';
import {
  Calendar, AlertTriangle, CheckCircle2, Clock, Loader2, Bell, ChevronLeft,
  Download, Users,
} from 'lucide-react';
const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_COLORS = {
  verified: '#34C759',
  pending: '#FF9500',
  rejected: '#FF3B30',
};

const EXPIRY_COLORS = {
  expired: '#FF3B30',
  critical: '#FF3B30',
  expiring_soon: '#FF9500',
  expiring: '#FFCC00',
  valid: '#34C759',
  none: '#9CA3AF',
};

export default function CertRenewalPipelinePage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const headers = { Authorization: `Bearer ${token}` };

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [horizon, setHorizon] = useState(12);
  const [notifying, setNotifying] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(null);

  useEffect(() => { load(); }, [horizon]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/certifications/renewal-pipeline?months=${horizon}`, { headers });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load pipeline');
    } finally { setLoading(false); }
  };

  const runScan = async () => {
    setNotifying(true);
    try {
      const res = await axios.post(`${API}/certifications/check-expiries`, {}, { headers });
      const total = (res.data.expired || 0) + (res.data['7'] || 0) + (res.data['30'] || 0) + (res.data['60'] || 0);
      toast.success(total > 0 ? `Dispatched ${total} renewal reminder(s)` : 'No new reminders needed');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Scan failed'); }
    finally { setNotifying(false); }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/certifications/renewal-pipeline/export-pdf?months=${horizon}`, {
        headers, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `KStreak_Renewal_Readiness_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Renewal Readiness PDF downloaded');
    } catch (e) { toast.error(e.response?.data?.detail || 'Download failed'); }
    finally { setDownloading(false); }
  };

  if (loading) return <div className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#9CA3AF]" /></div>;
  if (!data) return null;

  const maxCount = Math.max(1, ...data.months.map(m => m.count));

  return (
    <div className="max-w-6xl mx-auto p-4 space-y-4" data-testid="renewal-pipeline-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <Button variant="ghost" size="sm" onClick={() => navigate('/tests')} className="mb-1 -ml-2 h-7 text-xs text-[#4B5563]" data-testid="back-btn">
            <ChevronLeft className="w-3 h-3 mr-0.5" />Back to Capability Assessment
          </Button>
          <h1 className="text-2xl font-bold text-[#0A0A0A] flex items-center gap-2">
            <Calendar className="w-6 h-6 text-[#002FA7]" />Certification Renewal Pipeline
          </h1>
          <p className="text-sm text-[#4B5563] mt-1">
            Visual timeline of every certificate expiring in the next {horizon} months. Plan renewals proactively.
          </p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <select
            value={horizon}
            onChange={e => setHorizon(parseInt(e.target.value, 10))}
            className="h-8 text-xs border border-[#E5E7EB] rounded px-2"
            data-testid="horizon-select"
          >
            {[6, 12, 18, 24].map(m => <option key={m} value={m}>{m} months</option>)}
          </select>
          <Button size="sm" variant="outline" onClick={runScan} disabled={notifying} data-testid="notify-all-btn">
            {notifying ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Bell className="w-3 h-3 mr-1" />}
            Notify All Owners
          </Button>
          <Button size="sm" onClick={downloadPdf} disabled={downloading} className="bg-[#002FA7] hover:bg-[#00227A]" data-testid="download-pdf-btn">
            {downloading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Download className="w-3 h-3 mr-1" />}
            Download PDF
          </Button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-3" data-testid="pipeline-summary">
        <Card className="border-[#E5E7EB]">
          <CardContent className="p-3 text-center">
            <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wider">Upcoming renewals</p>
            <p className="text-2xl font-bold text-[#002FA7] mt-1">{data.upcoming_count}</p>
          </CardContent>
        </Card>
        <Card className="border-[#E5E7EB]">
          <CardContent className="p-3 text-center">
            <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wider">Already expired</p>
            <p className={`text-2xl font-bold mt-1 ${data.expired_count > 0 ? 'text-[#FF3B30]' : 'text-[#34C759]'}`}>{data.expired_count}</p>
          </CardContent>
        </Card>
        <Card className="border-[#E5E7EB]">
          <CardContent className="p-3 text-center">
            <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wider">Horizon</p>
            <p className="text-2xl font-bold text-[#0A0A0A] mt-1">{horizon}m</p>
          </CardContent>
        </Card>
      </div>

      {/* Expired backlog */}
      {data.expired_backlog.length > 0 && (
        <Card className="border-[#FF3B30]/30 bg-[#FF3B30]/5" data-testid="expired-backlog">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-[#FF3B30]">
              <AlertTriangle className="w-4 h-4" />Expired — needs immediate action ({data.expired_backlog.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-2">
              {data.expired_backlog.map(c => (
                <CertRow key={c.cert_id} cert={c} />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Timeline */}
      <Card className="border-[#E5E7EB]" data-testid="timeline-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Month-by-month breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          {/* Bar chart */}
          <div className="flex items-end gap-1 h-32 mb-4" data-testid="pipeline-chart">
            {data.months.map(m => {
              const heightPct = (m.count / maxCount) * 100;
              const verifiedPct = m.count ? (m.status_breakdown.verified / m.count) * 100 : 0;
              const pendingPct = m.count ? (m.status_breakdown.pending / m.count) * 100 : 0;
              const rejectedPct = m.count ? (m.status_breakdown.rejected / m.count) * 100 : 0;
              const isSelected = selectedMonth === m.key;
              return (
                <button
                  key={m.key}
                  onClick={() => setSelectedMonth(isSelected ? null : m.key)}
                  className={`flex-1 flex flex-col items-center gap-1 cursor-pointer transition-opacity ${selectedMonth && !isSelected ? 'opacity-40' : ''}`}
                  data-testid={`month-bar-${m.key}`}
                >
                  <span className="text-[9px] font-semibold text-[#0A0A0A]">{m.count || ''}</span>
                  <div className="w-full flex-1 flex items-end">
                    {m.count > 0 ? (
                      <div className="w-full flex flex-col-reverse rounded-t overflow-hidden" style={{ height: `${Math.max(heightPct, 8)}%`, minHeight: 4 }}>
                        {verifiedPct > 0 && <div style={{ height: `${verifiedPct}%`, backgroundColor: STATUS_COLORS.verified }} />}
                        {pendingPct > 0 && <div style={{ height: `${pendingPct}%`, backgroundColor: STATUS_COLORS.pending }} />}
                        {rejectedPct > 0 && <div style={{ height: `${rejectedPct}%`, backgroundColor: STATUS_COLORS.rejected }} />}
                      </div>
                    ) : (
                      <div className="w-full bg-[#F3F4F6] rounded-t" style={{ height: '4px' }} />
                    )}
                  </div>
                  <span className={`text-[9px] ${isSelected ? 'text-[#002FA7] font-semibold' : 'text-[#9CA3AF]'}`}>{m.label.split(' ')[0]}</span>
                </button>
              );
            })}
          </div>

          {/* Legend */}
          <div className="flex items-center gap-4 text-[10px] text-[#4B5563] mb-3">
            <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: STATUS_COLORS.verified }} />Verified</span>
            <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: STATUS_COLORS.pending }} />Pending review</span>
            <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: STATUS_COLORS.rejected }} />Rejected</span>
            <span className="ml-auto text-[#9CA3AF]">Click a bar to filter below</span>
          </div>

          {/* Selected-month detail or full list */}
          <div className="border-t border-[#E5E7EB] pt-3">
            {(selectedMonth
              ? data.months.find(m => m.key === selectedMonth)?.certs || []
              : data.months.flatMap(m => m.certs)
            ).length === 0 ? (
              <div className="text-center py-6 text-[#9CA3AF] text-sm">
                <CheckCircle2 className="w-8 h-8 mx-auto mb-1 text-[#E5E7EB]" />
                No certificates {selectedMonth ? `in ${data.months.find(m => m.key === selectedMonth)?.label}` : `in next ${horizon} months`}
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wider">
                  {selectedMonth
                    ? `${data.months.find(m => m.key === selectedMonth)?.label} renewals`
                    : `All ${data.upcoming_count} upcoming renewals`}
                </p>
                {(selectedMonth
                  ? data.months.find(m => m.key === selectedMonth)?.certs || []
                  : data.months.flatMap(m => m.certs)
                ).map(c => <CertRow key={c.cert_id} cert={c} />)}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function CertRow({ cert }) {
  const statusColor = STATUS_COLORS[cert.status] || '#9CA3AF';
  const exColor = EXPIRY_COLORS[cert.expiry_status] || '#9CA3AF';
  const days = cert.days_until_expiry;
  const exLabel = days !== null && days !== undefined
    ? (days < 0 ? `Expired ${-days}d ago` : days === 0 ? 'Expires today' : `${days}d remaining`)
    : '';
  return (
    <div className="flex items-center justify-between gap-2 p-2 bg-white border border-[#E5E7EB] rounded" data-testid={`cert-row-${cert.cert_id}`}>
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <Badge className="text-[8px] flex-shrink-0" style={{ backgroundColor: statusColor + '20', color: statusColor }}>{cert.status}</Badge>
        <div className="min-w-0">
          <p className="text-sm font-medium text-[#0A0A0A] truncate">{cert.certification_name} — <span className="text-[#4B5563] font-normal">{cert.user_name}</span></p>
          <p className="text-[10px] text-[#9CA3AF]">Expires {cert.expiry_date}</p>
        </div>
      </div>
      {exLabel && (
        <Badge className="text-[8px] flex-shrink-0 flex items-center gap-0.5" style={{ backgroundColor: exColor + '15', color: exColor }}>
          <Clock className="w-2.5 h-2.5" />{exLabel}
        </Badge>
      )}
    </div>
  );
}
